import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import {
   SettingsField,
   SettingsLbl,
   SettingsInput,
   SettingsHintSm,
} from './ispSettingsUI';
import {
   getIspPlans,
   getIspServers,
   type IspAccountDraft,
} from '@/api/isp';

/**
 * A subscriber line — one plan, on one router, for one customer.
 *
 * Opened straight after saving a customer. Ray, 2026-08-31: *"in the customer
 * registration, we also have to include proceed account creation in that one
 * customer modal right?"* Yes — a customer is created IN ORDER to give them a
 * line, and making somebody save, navigate to Accounts and search for the person
 * they just typed in is three screens for one job.
 *
 * ⚠️ DUE DAY IS PER ACCOUNT, NOT PER CUSTOMER (Ray, 2026-08-28). A restaurant
 * owner with Resto 1 and Resto 2 installed months apart bills on different days,
 * and that single example decides the field lives here rather than on the
 * customer.
 */
interface Props {
   customerId: string;
   customerName: string;
   onCancel: () => void;
   /*
    * 🔴 MAY RETURN A PROMISE, AND THE FORM AWAITS IT.
    *
    * Writing a PPPoE login goes over the tunnel to the MikroTik and takes
    * seconds. It used to be fire-and-forget, so the form had no idea it was
    * working: the button stayed bright and idle and an operator pressing
    * "Add connection" saw nothing happen at all. Ray, 2026-09-04: *"no loading
    * state so it looks hang or nothing happens."*
    *
    * Returning the promise is what lets the button settle by itself — on
    * success the modal closes, and on a refusal (a points shortfall, a
    * duplicate username) it comes back so the operator can fix and retry.
    */
   onSave: (draft: IspAccountDraft) => void | Promise<unknown>;
   /** Shown while the parent is writing to the router. */
   saving?: boolean;
   /**
    * Pre-fill, for the approve-an-application flow.
    *
    * Ray, 2026-09-01: *"when approve it should continue to the account
    * creation/connection form and it should automatically selected the plan he
    * select during application."* Everything here is a DEFAULT the operator can
    * overrule — the applicant asked for a plan, they did not buy one.
    */
   suggestedPlan?: string;
   /** Seeds the PPPoE login from their email, so it is one they can remember. */
   suggestedUsername?: string;
}

/**
 * A PPPoE username from an email address.
 *
 * The part before the @, stripped to what RouterOS is happy with. Ray,
 * 2026-09-01: *"the pppoe username i think auto fillup by their email."* It is
 * a login the customer can actually remember when they ring up, unlike a
 * generated string — and it is still editable, because two customers on the
 * same domain can collide.
 */
export const usernameFromEmail = (email: string): string =>
   String(email ?? '')
      .split('@')[0]
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, '')
      .slice(0, 32);

/**
 * 🔴 The default PPPoE password.
 *
 * Ray, 2026-09-01: *"the password default to 1234."* Deliberately trivial: it
 * is typed into a router by an installer standing in somebody's house, and it
 * guards a PPPoE session that is already bound to that line. It is editable,
 * and an operator who wants a real one types it.
 */
export const DEFAULT_PPPOE_PASSWORD = '1234';

/**
 * 1 to 30. The 31st is left out on purpose: it does not exist in half the
 * year, and a bill day that silently moves is a bill day an operator cannot
 * plan around.
 */
/** Shared with the Edit modal, so the two ask the same question. */
export const DAY_CHOICES = Array.from({ length: 30 }, (_, i) => i + 1);

export const ordinal = (n: number) => {
   const s = ['th', 'st', 'nd', 'rd'];
   const v = n % 100;
   return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

/**
 * The date their first bill actually lands.
 *
 * If that day has already passed this month, it is next month — which is the
 * bit an operator gets wrong when all they can see is a number.
 */
export const firstBillLabel = (day: number) => {
   if (!Number.isFinite(day) || day < 1) return '—';
   const now = new Date();
   const thisMonth = new Date(now.getFullYear(), now.getMonth(), day);
   const when =
      thisMonth.getTime() > now.getTime()
         ? thisMonth
         : new Date(now.getFullYear(), now.getMonth() + 1, day);
   return when.toLocaleDateString('en-PH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
   });
};

const IspAccountForm: React.FC<Props> = ({
   customerId,
   customerName,
   onCancel,
   onSave,
   saving,
   suggestedPlan,
   suggestedUsername,
}) => {
   const { data: servers = [] } = useQuery({
      queryKey: ['isp-servers'],
      queryFn: getIspServers,
   });
   const { data: plans = [] } = useQuery({
      queryKey: ['isp-plans'],
      queryFn: getIspPlans,
   });

   const [server, setServer] = useState('');
   const [plan, setPlan] = useState('');
   /*
    * 🔴 THE ANSWER IS ALREADY ON THE SCREEN — it is today. Ray, 2026-09-03:
    * *"if you put that 30 not 1 maybe i understand that right away. but please
    * improve this to make more answer predictive."*
    *
    * An operator adding somebody on the 30th almost always collects on the
    * 30th; that is when they installed it and when they will be back. Opening
    * on `1` asked them to decode the field before they could even agree with
    * it. Opening on today means the common case needs no thought at all, and
    * the preview underneath says the date out loud either way.
    */
   const [dueDay, setDueDay] = useState(String(new Date().getDate()));
   const [pppoeUsername, setUsername] = useState(suggestedUsername ?? '');
   const [pppoePassword, setPassword] = useState(DEFAULT_PPPOE_PASSWORD);
   const [errors, setErrors] = useState<Record<string, boolean>>({});
   const [busy, setBusy] = useState(false);
   const [message, setMessage] = useState('');

   // One router, or one plan on it — pick it rather than making them confirm the
   // only possible answer.
   useEffect(() => {
      if (!server && servers.length === 1) setServer(servers[0].id);
   }, [servers, server]);

   /**
    * Pre-select the plan the applicant asked for — once, and only if that plan
    * actually runs on the chosen router.
    *
    * `touchedPlan` stops this fighting the operator: without it, clearing the
    * selection would silently put it back and they would think the form was
    * broken.
    */
   const [touchedPlan, setTouchedPlan] = useState(false);
   useEffect(() => {
      if (touchedPlan || plan || !suggestedPlan) return;
      const ok = plans.find(
         (p) => p.id === suggestedPlan && (!server || p.servers.includes(server))
      );
      if (ok) setPlan(ok.id);
   }, [suggestedPlan, plans, server, plan, touchedPlan]);

   /**
    * 🔴 ONLY PLANS THAT RUN ON THE CHOSEN ROUTER.
    *
    * A plan enforces itself through a /ppp/profile that lives on ONE box. Put a
    * customer on a plan the router has never heard of and RouterOS falls back to
    * its default profile — they get whatever that allows, at the price of the
    * plan they picked, and nothing in SmartFi looks wrong.
    */
   const usable = plans.filter((p) => !server || p.servers.includes(server));

   useEffect(() => {
      if (plan && !usable.some((p) => p.id === plan)) setPlan('');
   }, [server, plan, usable]);

   const chosenPlan = plans.find((p) => p.id === plan);

   /** Either the parent said so, or we are awaiting its promise. */
   const working = saving || busy;

   const submit = async () => {
      if (busy) return; // a second press must not write a second line
      const next: Record<string, boolean> = {};
      if (!server) next.server = true;
      if (!plan) next.plan = true;
      if (pppoeUsername.trim().length < 3) next.pppoeUsername = true;
      if (pppoePassword.trim().length < 4) next.pppoePassword = true;
      const day = Number(dueDay);
      if (!Number.isFinite(day) || day < 1 || day > 30) next.dueDay = true;
      setErrors(next);
      if (Object.keys(next).length) {
         setMessage('Check the highlighted fields.');
         return;
      }
      setMessage('');
      setBusy(true);
      try {
         await onSave({
            customer: customerId,
            server,
            plan,
            serverName: servers.find((s) => s.id === server)?.name ?? '',
            dueDay: Math.trunc(day),
            pppoeUsername: pppoeUsername.trim(),
            pppoePassword: pppoePassword.trim(),
         });
      } catch {
         // The parent reports the reason — a toast for a shortfall, the field
         // message for a duplicate username. Swallowed here so the button is
         // released rather than left spinning on a failure the operator can act
         // on.
      } finally {
         setBusy(false);
      }
   };

   return (
      <Wrapper>
         <Lead>
            A connection for <strong>{customerName}</strong>. SmartFi writes the
            PPPoE login onto the router as it saves, so they can get online
            straight away.
         </Lead>

         <Grid>
            <SettingsField>
               <SettingsLbl>Which MikroTik</SettingsLbl>
               <Sel
                  value={server}
                  $err={!!errors.server}
                  onChange={(e) => setServer(e.target.value)}
               >
                  <option value="">Choose a router</option>
                  {servers.map((s) => (
                     <option key={s.id} value={s.id}>
                        {s.name}
                        {s.setupStage === 'ready' ? '' : ' — not reporting'}
                     </option>
                  ))}
               </Sel>
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Internet plan</SettingsLbl>
               <Sel
                  value={plan}
                  $err={!!errors.plan}
                  onChange={(e) => {
                     setTouchedPlan(true);
                     setPlan(e.target.value);
                  }}
               >
                  <option value="">Choose a plan</option>
                  {usable.map((p) => (
                     <option key={p.id} value={p.id}>
                        {p.name} — ₱{p.price.toLocaleString()}/mo
                     </option>
                  ))}
               </Sel>
               <SettingsHintSm>
                  {!server
                     ? 'Pick a router first — a plan is enforced by a profile that lives on one.'
                     : usable.length === 0
                       ? 'No plan runs on that router yet. Link one in Internet plans first.'
                       : chosenPlan && !chosenPlan.profileSyncedAt
                         ? `${chosenPlan.name} has not been written to the router yet. Save it once in Internet plans and the speed will apply.`
                         : 'Only plans that run on the chosen router.'}
               </SettingsHintSm>
            </SettingsField>

            <SettingsField>
               <SettingsLbl>PPPoE username</SettingsLbl>
               <Field
                  value={pppoeUsername}
                  $err={!!errors.pppoeUsername}
                  placeholder="e.g. juan.delacruz"
                  autoCapitalize="none"
                  spellCheck={false}
                  onChange={(e) => setUsername(e.target.value)}
               />
               <SettingsHintSm>
                  What their ONU signs in with. Must be unique on this router.
               </SettingsHintSm>
            </SettingsField>

            <SettingsField>
               <SettingsLbl>PPPoE password</SettingsLbl>
               <Field
                  value={pppoePassword}
                  $err={!!errors.pppoePassword}
                  placeholder="at least 4 characters"
                  autoCapitalize="none"
                  spellCheck={false}
                  onChange={(e) => setPassword(e.target.value)}
               />
               <SettingsHintSm>
                  Stored as typed — the ONU has to send the real characters.
               </SettingsHintSm>
            </SettingsField>

            {/* 🔴 A NUMBER FROM 1 TO 30 IS NOT A QUESTION ANYBODY CAN ANSWER.
                Ray, 2026-09-03, after finally working out what it meant: *"this
                is confusing to operators… make this friendlier to idiots.
                replace that with simple to fillup even a kindergarten can get
                it."*

                He is right, and the fix is not a better label — it is asking a
                question that has an obvious answer. "When do you collect from
                them?" is a thing an operator already knows; "bills on day" is
                a field they have to decode first.

                The preview does the rest: it says the actual date of the first
                bill, so nobody has to imagine what the number will do. */}
            <SettingsField>
               <SettingsLbl>When do you collect from them?</SettingsLbl>
               <Sel
                  value={dueDay}
                  $err={!!errors.dueDay}
                  onChange={(e) => setDueDay(e.target.value)}
               >
                  {DAY_CHOICES.map((d) => (
                     <option key={d} value={String(d)}>
                        Every month on the {ordinal(d)}
                        {d === new Date().getDate() ? ' — today' : ''}
                     </option>
                  ))}
               </Sel>
               <BillPreview>
                  First bill: <strong>{firstBillLabel(Number(dueDay))}</strong>
               </BillPreview>
            </SettingsField>
         </Grid>

         {message && <Err>{message}</Err>}

         <Actions>
            <Ghost type="button" onClick={onCancel} disabled={working}>
               Skip for now
            </Ghost>
            <Primary type="button" onClick={submit} disabled={working}>
               {working ? 'Writing to the router…' : 'Add connection'}
            </Primary>
         </Actions>
      </Wrapper>
   );
};

export default IspAccountForm;

const Wrapper = styled.div`
   width: 620px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 12px;
`;

const Lead = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 12px 16px;
   @media (max-width: 640px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const Field = styled(SettingsInput)<{ $err?: boolean }>`
   border-color: ${({ $err }) => ($err ? colors.DANGER : colors.GRAY12)};
`;

const BillPreview = styled.p`
   margin: 6px 0 0;
   font-size: 12.5px;
   color: ${colors.GRAY5};

   strong {
      color: ${colors.MAIN_DARK};
   }
`;
const Sel = styled.select<{ $err?: boolean }>`
   padding: 8px 10px;
   border: 1px solid ${({ $err }) => ($err ? colors.DANGER : colors.GRAY12)};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   width: 100%;
`;

const Err = styled.p`
   margin: 0;
   font-size: 12px;
   color: ${colors.DANGER};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const Primary = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;
